import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  CreateButton,
  Datagrid,
  FunctionField,
  List,
  ReferenceField,
  SelectField,
  SimpleList,
  TextField,
  Title,
  TopToolbar,
  useGetOne,
  useStore,
} from "react-admin";
import { Link } from "react-router-dom";
import { Box, Paper, Typography, useMediaQuery } from "@mui/material";
import {
  APPLICATIONS,
  HEALTHCARE_CAMPAIGNS,
  HEALTHCARE_CAMPAIGNS_APPLICATIONS,
  HEALTHCARE_CAMPAIGNS_APPLICATIONS_DETAILS,
  MOBILE_BREAKPOINT,
  SpecialistServicePath,
} from "utils/constants";
import { HealthcareApplicationOriginChoices } from "utils/choices";
import CustomFilter from "features/CustomFilter";
import dataProvider from "server/dataProvider";
import { settings } from "app";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ChangeStatusDialog from "./ChangeStatusDialog";
import { isAcceptingApplications } from "./utils";
import { specialistRequests } from "api/specialistApi";
import { useNotifyError } from "utils/useNotifyError";
import { MobileListRow } from "features/MobileListRow";
import { buildQueryString } from "utils/helpers";

export const HEALTHCARE_CAMPAIGNS_APPLICATIONS_STORE_KEY = "healthcareCampaignApplications.filter";

const ListActions = ({ campaignId }) => {
  const { data: campaign } = useGetOne(SpecialistServicePath + HEALTHCARE_CAMPAIGNS, { id: campaignId });

  return (
    <TopToolbar>
      {isAcceptingApplications(campaign) && (
        <SecuredComponent permission={permissions.canManageHealthcareCampaignApplications}>
          <CreateButton
            resource={SpecialistServicePath + HEALTHCARE_CAMPAIGNS_APPLICATIONS}
            label="Добави кандидатура"
          />
        </SecuredComponent>
      )}
    </TopToolbar>
  );
};

const ListHealthcareCampaignApplication = () => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const notifyError = useNotifyError();

  const [campaignsOptions, setCampaignsOptions] = useState([]);
  // filters drives the <List> and the gate (persisted via useStore);
  // campaignId is the draft bound to CustomFilter, committed on "Провери".
  const [filters, setFilters] = useStore(HEALTHCARE_CAMPAIGNS_APPLICATIONS_STORE_KEY, null);
  const [campaignId, setCampaignId] = useState(filters?.campaignId ?? null);

  const getCampaigns = useCallback(async () => {
    try {
      const { data } = await dataProvider.customRequest(specialistRequests.getHealthcareCampaigns());

      const options = (data?.items ?? []).map(c => ({ ...c, name: c.coursesYear }));
      setCampaignsOptions(options);
    } catch (err) {
      notifyError(err);
    }
  }, [notifyError]);

  useEffect(() => {
    getCampaigns();
  }, [getCampaigns]);

  // Both ids go in the URL (not router state) so the details page survives a reload or re-login,
  // which also lets the "Детайли" buttons be real links (component={Link}) - openable in a new tab.
  const getDetailsUrl = applicationId =>
    `/${SpecialistServicePath}${HEALTHCARE_CAMPAIGNS_APPLICATIONS_DETAILS}${buildQueryString({
      campaignId: filters?.campaignId,
      applicationId,
    })}`;

  const handleSearch = () => {
    if (campaignId) {
      setFilters({ campaignId });
    }
  };

  return (
    <>
      {!filters && <Title title={"Кандидатури - кампании за здравни грижи"} />}
      <CustomFilter
        title="Списък кампании"
        filters={[
          {
            label: "Кампания",
            value: campaignId,
            setValue: setCampaignId,
            type: "select",
            multiple: false,
            choices: campaignsOptions,
          },
        ]}
        cardWidth={isMobile ? "350px" : "700px"}
        buttonLabel="Провери"
        buttonOnClick={handleSearch}
      />
      {!filters && (
        <Paper>
          <Box mt={3}>
            <Typography
              variant="inherit"
              align="center"
              color={"secondary"}
            >
              Въведи данни за търсене на кандидатури.
            </Typography>
          </Box>
        </Paper>
      )}
      {filters && (
        <List
          resource={`${SpecialistServicePath}${HEALTHCARE_CAMPAIGNS}/${filters.campaignId}/${APPLICATIONS}`}
          exporter={false}
          actions={<ListActions campaignId={filters.campaignId} />}
          title="Кандидатури - кампании за здравни грижи"
        >
          {isMobile ? (
            <SimpleList
              linkType={false}
              sx={settings.style.mobileList}
              primaryText={record => (
                <MobileListRow
                  fields={[
                    {
                      label: "Имена",
                      value: record?.secondName
                        ? `${record?.firstName} ${record?.secondName} ${record?.lastName}`
                        : `${record?.firstName} ${record?.lastName}`,
                    },
                  ]}
                />
              )}
              secondaryText={record => (
                <div>
                  <MobileListRow
                    fields={[
                      {
                        label: "Специалност на кандидата",
                        value: (
                          <ReferenceField
                            source="participantSpeciality"
                            reference={specialistRequests.getHealthcareSpecialitySelect()}
                            sortable={false}
                          >
                            <TextField source="nameBg" />
                          </ReferenceField>
                        ),
                      },
                    ]}
                  />
                  <MobileListRow fields={[{ label: "ЕГН/ЛНЧ", value: record?.personId }]} />
                  <MobileListRow fields={[{ label: "УИН", value: record?.uin }]} />
                  <MobileListRow fields={[{ label: "Курс здравна грижа", value: record?.courseNameBg }]} />
                  <MobileListRow fields={[{ label: "Статус кандидатура", value: record?.statusNameBg }]} />
                  <MobileListRow
                    fields={[
                      {
                        label: "Произход",
                        value: (
                          <SelectField
                            source="origin"
                            choices={HealthcareApplicationOriginChoices}
                          />
                        ),
                      },
                    ]}
                  />
                  <SecuredComponent permission={permissions.canChangeHealthcareCampaignApplicationsStatuses}>
                    <ChangeStatusDialog
                      campaignId={filters.campaignId}
                      applicationId={record?.id}
                      currentStatus={record?.statusId}
                      currentStatusName={record?.statusNameBg}
                    />
                  </SecuredComponent>
                </div>
              )}
              tertiaryText={record => (
                <SecuredComponent permission={permissions.canPreviewHealthcareCampaignApplications}>
                  {/* Icon is passed as a child instead of the original startIcon prop: on mobile
                      react-admin's Button renders only its children, so startIcon left it empty. */}
                  <Button
                    label=""
                    component={Link}
                    to={getDetailsUrl(record?.id)}
                  >
                    <VisibilityIcon />
                  </Button>
                </SecuredComponent>
              )}
            />
          ) : (
            <Datagrid
              bulkActionButtons={false}
              rowClick={false}
              sx={settings.style.firstExpandIconDatagrid}
            >
              <FunctionField
                label="Имена"
                render={rec =>
                  rec?.secondName
                    ? `${rec?.firstName} ${rec?.secondName} ${rec?.lastName}`
                    : `${rec?.firstName} ${rec?.lastName}`
                }
              />
              <ReferenceField
                source="participantSpeciality"
                reference={specialistRequests.getHealthcareSpecialitySelect()}
                label="Специалност на кандидата"
              >
                <TextField source="nameBg" />
              </ReferenceField>
              <TextField
                source="personId"
                label="ЕГН/ЛНЧ"
                sortable={false}
              />
              <TextField
                source="uin"
                label="УИН"
                sortable={false}
              />
              <TextField
                source="courseNameBg"
                label="Курс здравна грижа"
                sortable={false}
              />
              <TextField
                source="statusNameBg"
                label="Статус кандидатура"
                sortable={false}
              />
              <SelectField
                source="origin"
                label="Произход"
                choices={HealthcareApplicationOriginChoices}
                sortable={false}
              />

              <FunctionField
                render={rec => (
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <SecuredComponent permission={permissions.canChangeHealthcareCampaignApplicationsStatuses}>
                      <ChangeStatusDialog
                        campaignId={filters.campaignId}
                        applicationId={rec?.id}
                        currentStatus={rec?.statusId}
                        currentStatusName={rec?.statusNameBg}
                      />
                    </SecuredComponent>
                    <SecuredComponent permission={permissions.canPreviewHealthcareCampaignApplications}>
                      {/* Icon as a child (was startIcon) - same markup as the mobile variant. */}
                      <Button
                        label="Детайли"
                        component={Link}
                        to={getDetailsUrl(rec?.id)}
                      >
                        <VisibilityIcon />
                      </Button>
                    </SecuredComponent>
                  </Box>
                )}
              />
            </Datagrid>
          )}
        </List>
      )}
    </>
  );
};

export default ListHealthcareCampaignApplication;
